import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons/faCircleExclamation';
import { TypedMemo } from '../../../common/types';
import { AssetShareModel } from './useAssetShare';
import { AssetViewerItem } from './viewerItems';
import { viewerFacts, viewerFactsLine } from './viewerFacts';
import { viewerMessages } from './viewerMessages';
import { ViewerActions } from './ViewerActions';
import { ViewerShareBox } from './ViewerShareBox';
import { ViewerProRes } from './ViewerProRes';
import { hasProResDownload } from './proResView';
import { RevisionEntry } from '../revisions/revisionModel';
import type { ViewerRevisionAction } from './viewerRevisionAction';
import { revisionMessages } from '../revisions/revisionMessages';
import { WatermarkCallout } from '../../watermarkUpsell/files/WatermarkCallout';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the upsell; each part uses its own slice of it */
import upsellStyles from '../../watermarkUpsell/WatermarkUpsell.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the viewer; each part uses its own slice of it */
import styles from './AssetViewer.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ViewerSidePanelProps {
  accountId: number;
  catalogItemUuid: string;
  item: AssetViewerItem;
  label: string;
  /** "Forests · LaziiBee · Noir". */
  songLine: string;
  songTitle: string;
  isNarrow: boolean;
  isDemo: boolean;
  canPost: boolean;
  /** Null on the demo artist: no share box (design a5). */
  share: AssetShareModel | null;
  videoFailed: boolean;
  onPost: (item: AssetViewerItem) => void;
  /** "New revision" (design d1, d3). */
  onNewRevision: () => void;
  canMakeRevision: boolean;
  /** What makes the next revision of the file (`viewerRevisionAction`). */
  revisionAction: ViewerRevisionAction;
  onEditVideo: () => void;
  /** The revision history (design d1); null when the item has none. */
  revisions: React.ReactNode;
  /** An old revision whose file is gone (design d4): no file actions, "Make it again as rN". */
  goneEntry: RevisionEntry | null;
  nextNumber: number;
}

/**
 * The side panel (designs a1, a2, a5) and, below 700 px, the sheet under the file (a6): the asset
 * label, the song line, the facts, the actions, the share box, the revision history (Releases v2,
 * d1, d5) and the notes. An old revision whose file is gone (d4) shows only "Make it again as rN".
 * A watermarked file shows the callout and its facts in pink (design: trial-upsell a3).
 */
const ViewerSidePanelComponent: React.FC<ViewerSidePanelProps> = (props) => {
  const { item, label, songLine, isNarrow, isDemo, canPost, share, videoFailed, onPost } = props;
  const { onNewRevision, canMakeRevision, revisions, goneEntry, nextNumber, accountId, catalogItemUuid } = props;
  const { revisionAction, onEditVideo, songTitle } = props;
  const intl = useIntl();
  const facts = useMemo(() => viewerFacts(intl, item), [intl, item]);
  const [sheetOpen, setSheetOpen] = useState(false);
  useEffect(() => setSheetOpen(false), [item.key]);
  const openSheet = useCallback(() => setSheetOpen(true), []);
  // Below 700 px the box shows after "Link on", and for a result the user must see.
  const showBox =
    goneEntry === null &&
    share !== null &&
    (!isNarrow || sheetOpen || share.status === 'turnedOff' || share.error !== null || share.confirmOpen);

  return (
    <aside className={styles.side} data-testid="viewer-side">
      <div>
        <h2 className={styles.name} id="viewer-asset-name">
          {label}
        </h2>
        <div className={styles.sub}>{withBrandWord(isNarrow ? viewerFactsLine(intl, item) : songLine)}</div>
      </div>
      {!isNarrow && goneEntry === null && (
        <dl className={styles.facts}>
          {facts.map((fact) => (
            <React.Fragment key={fact.key}>
              <dt>{intl.formatMessage(fact.label)}</dt>
              <dd className={fact.bad === true ? upsellStyles.factBad : undefined}>{withBrandWord(fact.value)}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
      {/* design: trial-upsell a3. Always on a watermarked file, on Free and the trial. */}
      {goneEntry === null && item.watermark && (
        <WatermarkCallout variant="file" surface="viewer" preview={item.preview} />
      )}
      {goneEntry === null ? (
        <ViewerActions
          item={item}
          label={label}
          songTitle={songTitle}
          isDemo={isDemo}
          canPost={canPost}
          onPost={onPost}
          onNewRevision={onNewRevision}
          canMakeRevision={canMakeRevision}
          revisionAction={revisionAction}
          onEditVideo={onEditVideo}
          narrowShare={isNarrow ? share : null}
          onOpenShareSheet={openSheet}
        />
      ) : (
        revisionAction.kind === 'newRevision' && (
          <div className={styles.acts}>
            <button
              type="button"
              className={clsx(styles.btn, styles.btnPrimary, styles.full)}
              disabled={isDemo}
              onClick={onNewRevision}
              data-testid="viewer-make-again-as"
            >
              {intl.formatMessage(revisionMessages.makeAgainAs, { number: nextNumber })}
            </button>
          </div>
        )
      )}
      {goneEntry === null && hasProResDownload(item) && (
        <ViewerProRes accountId={accountId} catalogItemUuid={catalogItemUuid} item={item} isDemo={isDemo} />
      )}
      {videoFailed && (
        <div className={clsx(styles.note, styles.noteWarn)} role="alert">
          <FontAwesomeIcon icon={faCircleExclamation} />
          <span>{intl.formatMessage(viewerMessages.videoError)}</span>
        </div>
      )}
      {showBox && share !== null && <ViewerShareBox model={share} />}
      {revisions}
      {isDemo && (
        <div className={styles.note} data-testid="viewer-demo-note">
          <FontAwesomeIcon icon={faLock} />
          <span>{intl.formatMessage(viewerMessages.demoNote)}</span>
        </div>
      )}
    </aside>
  );
};

export const ViewerSidePanel = TypedMemo(ViewerSidePanelComponent);
